/**
 * Utilities for working with external CMS systems (Filevine, Neos, etc.)
 */

/**
 * Document sync target systems that support blob sink operations.
 * This should match backend app/defs/external_document.py BlobSinkActionTargetSystem enum.
 * Note: The generated types may lag behind - this local type ensures compatibility.
 */
export type DocumentSyncTargetSystem = 'FILEVINE' | 'CLIO' | 'NEOS' | 'SMARTADVOCATE' | 'LITIFY' | 'CASEPEER' | 'DROPBOX' | 'GOOGLE_DRIVE';

/**
 * CMS target systems that can be detected via URL pattern matching.
 * DROPBOX and GOOGLE_DRIVE are excluded — they use `external_drive_url`,
 * not `external_cms_url`.
 */
type UrlDetectableSystem = Exclude<DocumentSyncTargetSystem, 'DROPBOX' | 'GOOGLE_DRIVE'>;

/**
 * Mapping of CMS target systems to URL patterns used to detect them.
 * Mirrors backend app/domain/cms/common.py CMS_URL_MAP
 *
 * Note: DROPBOX and GOOGLE_DRIVE are intentionally excluded — they are
 * stored in `external_drive_url`, not `external_cms_url`.
 */
const CMS_URL_PATTERNS: Record<UrlDetectableSystem, string[]> = {
  FILEVINE: ['filevine.com', 'filevineapp.com'],
  CLIO: ['app.clio.com', 'clio.com'],
  NEOS: ['app.neos-cloud.com', 'neos-cloud.com'],
  SMARTADVOCATE: ['app.smartadvocate.com', 'smartadvocate.com'],
  LITIFY: ['lightning.force.com'],
  CASEPEER: ['casepeer.com', 'my.casepeer.com'],
};

/**
 * Detect the CMS target system from an external CMS URL.
 * Returns the target system string if detected, null otherwise.
 *
 * @param url - The external CMS URL (e.g., "https://app.clio.com/matters/123")
 * @returns The detected target system or null if not detected
 *
 * @example
 * getCmsTargetSystemFromUrl("https://app.clio.com/matters/123") // "CLIO"
 * getCmsTargetSystemFromUrl("https://filevine.com/project/456") // "FILEVINE"
 * getCmsTargetSystemFromUrl("https://unknown.com") // null
 */
export const getCmsTargetSystemFromUrl = (
  url: string | null | undefined,
): DocumentSyncTargetSystem | null => {
  if (!url) return null;

  const lowerUrl = url.toLowerCase();

  const matchedEntry = Object.entries(CMS_URL_PATTERNS).find(([, patterns]) =>
    patterns.some((pattern) => lowerUrl.includes(pattern)),
  );

  return matchedEntry
    ? (matchedEntry[0] as DocumentSyncTargetSystem)
    : null;
};

/**
 * Detect the drive storage system from a matter's `external_drive_url`.
 * Supports both Google Drive and Dropbox folder URLs.
 *
 * @param url - The external drive URL
 * @returns the detected drive system or null
 */
export const getDriveSystemFromUrl = (
  url: string | null | undefined,
): 'GOOGLE_DRIVE' | 'DROPBOX' | null => {
  if (!url) return null;
  const lower = url.toLowerCase();
  if (lower.includes('drive.google.com')) return 'GOOGLE_DRIVE';
  if (lower.includes('dropbox.com')) return 'DROPBOX';
  return null;
};

/**
 * CMS sync capabilities — defines which CMS systems support automated document syncing.
 * Used by CaseInfoStep, CMSIntegrationSection, and DocumentsStep to display sync status
 * and conditionally render auto-sync UX.
 */
export type CmsSyncKey =
  | DocumentSyncTargetSystem
  | 'MYCASE';

export interface CmsSyncCapability {
  key: CmsSyncKey;
  name: string;
  hasAutoSync: boolean;
}

export const CMS_SYNC_CAPABILITIES: CmsSyncCapability[] = [
  { key: 'FILEVINE', name: 'Filevine', hasAutoSync: true },
  { key: 'CLIO', name: 'Clio', hasAutoSync: false },
  { key: 'NEOS', name: 'Neos', hasAutoSync: false },
  { key: 'SMARTADVOCATE', name: 'SmartAdvocate', hasAutoSync: false },
  { key: 'MYCASE', name: 'MyCase', hasAutoSync: false },
  { key: 'CASEPEER', name: 'CasePeer', hasAutoSync: true },
  { key: 'LITIFY', name: 'Litify', hasAutoSync: false },
  { key: 'DROPBOX', name: 'Dropbox', hasAutoSync: true },
  { key: 'GOOGLE_DRIVE', name: 'Google Drive', hasAutoSync: true },
];

/**
 * Get the display name for an external CMS system based on its source identifier.
 * Derives names from CMS_SYNC_CAPABILITIES to maintain a single source of truth.
 * @param source - The source identifier from the external task (e.g., "FILEVINE", "NEOS")
 * @returns The formatted display name for the system
 */
export const getExternalSystemDisplayName = (source: string): string => {
  const found = CMS_SYNC_CAPABILITIES.find(
    (cms) => cms.key === source.toUpperCase(),
  );
  return found?.name ?? source;
};

/**
 * Check if a detected CMS key supports automated document syncing.
 * @param cmsKey - The CMS key returned by getCmsTargetSystemFromUrl (e.g., "FILEVINE")
 * @returns true if the CMS supports auto-sync
 */
export const hasCmsAutoSync = (cmsKey: string | null | undefined): boolean => {
  if (!cmsKey) return false;
  return CMS_SYNC_CAPABILITIES.some((cms) => cms.key === cmsKey && cms.hasAutoSync);
};

/**
 * Maps PushChangeType (from PushChangesDialog) to ExternalTaskActionType (from API).
 * Used to check if an external task supports a specific action.
 */
export const pushChangeTypeToActionType = {
  status: 'CHANGE_STATUS',
  due_date: 'CHANGE_DUE_DATE',
  comment: 'ADD_COMMENT',
} as const;
